import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import {
  REGULATION_ASSESSMENT_PACK_PRIVILEGES,
  REGULATION_AUTO_TAGGING_PRIVILEGES,
} from "../../../services/permissionsService";
import {
  getRegulationAutoTagDetail,
  getRegulationConditionalTagDetail,
} from "../../../services/regulationTagDetailService";
import { AssessmentPackSection } from "./AssessmentPackSection";
import { ImpactAssessmentCard } from "./ImpactAssessmentCard";
import { MOCK_ASSET_ROWS } from "./mockData";
import { ShowAllImpactModal } from "./ShowAllImpactModal";
import {
  IMPACT_SUB_TABS,
  type EnhanceTaggingRow,
  type ImpactAssessmentRow,
  type ImpactSubTabKey,
} from "./types";

export interface TaggingImpactAssessmentSectionProps {
  regulationId?: string;
  version?: string;
  /** When true, show Sync to Task (task-linked flow). */
  showSyncToTask?: boolean;
  onSyncToTask?: () => void;
}

type LoadState = "idle" | "loading" | "success" | "error";

export const TaggingImpactAssessmentSection: React.FC<
  TaggingImpactAssessmentSectionProps
> = ({
  regulationId = "",
  version = "",
  showSyncToTask = false,
  onSyncToTask,
}) => {
  const canReadAssessmentPack = useHasPrivilege(
    REGULATION_ASSESSMENT_PACK_PRIVILEGES.read,
  );
  const canReadClassificationResults = useHasPrivilege(
    REGULATION_AUTO_TAGGING_PRIVILEGES.read,
  );
  const [activeSubTab, setActiveSubTab] =
    useState<ImpactSubTabKey>("taggingresults");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [enhanceCollapsed, setEnhanceCollapsed] = useState<
    Record<string, boolean>
  >({});
  const [showAllRow, setShowAllRow] = useState<ImpactAssessmentRow | null>(
    null,
  );

  const [taggingRows, setTaggingRows] = useState<ImpactAssessmentRow[] | null>(
    null,
  );
  const [taggingState, setTaggingState] = useState<LoadState>("idle");
  const [taggingError, setTaggingError] = useState<string | null>(null);

  const [enhanceRows, setEnhanceRows] = useState<EnhanceTaggingRow[] | null>(
    null,
  );
  const [enhanceState, setEnhanceState] = useState<LoadState>("idle");
  const [enhanceError, setEnhanceError] = useState<string | null>(null);

  const canFetch = Boolean(regulationId.trim() && version.trim());

  useEffect(() => {
    if (!canReadClassificationResults || activeSubTab !== "taggingresults") {
      return;
    }

    if (!canFetch) {
      setTaggingRows(null);
      setTaggingState("idle");
      setTaggingError(null);
      return;
    }

    let cancelled = false;
    setTaggingState("loading");
    setTaggingError(null);

    void getRegulationAutoTagDetail(regulationId, version)
      .then((res) => {
        if (cancelled) return;
        if (res.status === "error") {
          setTaggingRows(null);
          setTaggingState("error");
          setTaggingError(res.message || "Failed to load Classification results");
          return;
        }
        setTaggingRows(res.data ?? null);
        setTaggingState("success");
        setCollapsed({});
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTaggingRows(null);
        setTaggingState("error");
        setTaggingError(
          err instanceof Error ? err.message : "Failed to load Classification results",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeSubTab,
    canFetch,
    canReadClassificationResults,
    regulationId,
    version,
  ]);

  useEffect(() => {
    if (
      !canReadClassificationResults ||
      activeSubTab !== "enhancetaggingresults"
    ) {
      return;
    }

    if (!canFetch) {
      setEnhanceRows(null);
      setEnhanceState("idle");
      setEnhanceError(null);
      return;
    }

    let cancelled = false;
    setEnhanceState("loading");
    setEnhanceError(null);

    void getRegulationConditionalTagDetail(regulationId, version)
      .then((res) => {
        if (cancelled) return;
        if (res.status === "error") {
          setEnhanceRows(null);
          setEnhanceState("error");
          setEnhanceError(
            res.message || "Failed to load Conditional Classification results",
          );
          return;
        }
        setEnhanceRows(res.data ?? null);
        setEnhanceState("success");
        setEnhanceCollapsed({});
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEnhanceRows(null);
        setEnhanceState("error");
        setEnhanceError(
          err instanceof Error
            ? err.message
            : "Failed to load Conditional Classification results",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeSubTab,
    canFetch,
    canReadClassificationResults,
    regulationId,
    version,
  ]);

  const allTaggingCollapsed =
    (taggingRows?.length ?? 0) > 0 &&
    (taggingRows ?? []).every((r) => collapsed[r.id]);

  const allEnhanceCollapsed =
    (enhanceRows?.length ?? 0) > 0 &&
    (enhanceRows ?? []).every((r) => enhanceCollapsed[r.id]);

  const isCardListTab =
    activeSubTab === "taggingresults" ||
    activeSubTab === "enhancetaggingresults";

  const allCollapsed =
    activeSubTab === "enhancetaggingresults"
      ? allEnhanceCollapsed
      : allTaggingCollapsed;

  const toggleAll = () => {
    if (activeSubTab === "enhancetaggingresults") {
      if (allEnhanceCollapsed) {
        setEnhanceCollapsed({});
        return;
      }
      const next: Record<string, boolean> = {};
      (enhanceRows ?? []).forEach((r) => {
        next[r.id] = true;
      });
      setEnhanceCollapsed(next);
      return;
    }

    if (allTaggingCollapsed) {
      setCollapsed({});
      return;
    }
    const next: Record<string, boolean> = {};
    (taggingRows ?? []).forEach((r) => {
      next[r.id] = true;
    });
    setCollapsed(next);
  };

  const assetGroups = useMemo(() => {
    const order: Array<"High" | "Medium" | "Low"> = ["High", "Medium", "Low"];
    return order
      .map((tier) => {
        const groupRows = MOCK_ASSET_ROWS.filter((r) => r.relevance === tier);
        if (!groupRows.length) return null;
        return {
          tier,
          count: groupRows.length,
          relBg: groupRows[0].relBg,
          relColor: groupRows[0].relColor,
          rows: groupRows,
        };
      })
      .filter(Boolean) as Array<{
      tier: string;
      count: number;
      relBg: string;
      relColor: string;
      rows: typeof MOCK_ASSET_ROWS;
    }>;
  }, []);

  const renderTaggingBody = () => {
    if (!canFetch) {
      return (
        <Typography
          sx={{ color: "#6A7282", fontSize: 13, py: 4, textAlign: "center" }}
        >
          Regulation version is not ready yet.
        </Typography>
      );
    }
    if (taggingState === "loading" || taggingState === "idle") {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (taggingState === "error") {
      return (
        <Alert severity="error">
          {taggingError || "Failed to load Classification results"}
        </Alert>
      );
    }
    if (taggingRows == null) {
      return (
        <Typography
          sx={{ color: "#6A7282", fontSize: 13, py: 4, textAlign: "center" }}
        >
          No Classification results available.
        </Typography>
      );
    }
    if (taggingRows.length === 0) {
      return (
        <Typography
          sx={{ color: "#6A7282", fontSize: 13, py: 4, textAlign: "center" }}
        >
          No Classification tags applied.
        </Typography>
      );
    }
    return taggingRows.map((row) => (
      <ImpactAssessmentCard
        key={row.id}
        row={row}
        expanded={!collapsed[row.id]}
        onToggleExpand={() =>
          setCollapsed((c) => ({ ...c, [row.id]: !c[row.id] }))
        }
        onShowAll={() => setShowAllRow(row)}
      />
    ));
  };

  const renderEnhanceBody = () => {
    if (!canFetch) {
      return (
        <Typography
          sx={{ color: "#6A7282", fontSize: 13, py: 4, textAlign: "center" }}
        >
          Regulation version is not ready yet.
        </Typography>
      );
    }
    if (enhanceState === "loading" || enhanceState === "idle") {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (enhanceState === "error") {
      return (
        <Alert severity="error">
          {enhanceError || "Failed to load Conditional Classification results"}
        </Alert>
      );
    }
    if (enhanceRows == null) {
      return (
        <Typography
          sx={{ color: "#6A7282", fontSize: 13, py: 4, textAlign: "center" }}
        >
          No Conditional Classification results available.
        </Typography>
      );
    }
    if (enhanceRows.length === 0) {
      return (
        <Typography
          sx={{ color: "#6A7282", fontSize: 13, py: 4, textAlign: "center" }}
        >
          No Conditional Classification tags applied.
        </Typography>
      );
    }
    return enhanceRows.map((r) => {
      const expanded = !enhanceCollapsed[r.id];
      return (
        <Box
          key={r.id}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "10px",
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: 2,
              py: 1.5,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.125 }}>
                <Typography sx={{ fontSize: 11, color: "#99A1AF" }}>
                  {r.dimension}
                </Typography>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: "#101828" }}
                >
                  {r.tagValue}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  setEnhanceCollapsed((c) => ({
                    ...c,
                    [r.id]: !c[r.id],
                  }))
                }
                aria-label={expanded ? "Collapse" : "Expand"}
                sx={{ color: "#99A1AF", p: 0.25 }}
              >
                {expanded ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Box>
          {expanded ? (
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <Box
                sx={{
                  border: "1px solid #F3F4F6",
                  borderRadius: "8px",
                  bgcolor: "#F9FAFB",
                  px: 1.75,
                  py: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#6A7282",
                    fontWeight: 600,
                    mb: 1.25,
                  }}
                >
                  System
                </Typography>
                <Box>
                  <Typography
                    sx={{ fontSize: 11, color: "#99A1AF", mb: 0.5 }}
                  >
                    Matching Condition
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#101828" }}>
                    {r.matchingCondition}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      );
    });
  };

  const showCollapseToggle =
    isCardListTab &&
    ((activeSubTab === "taggingresults" &&
      taggingState === "success" &&
      (taggingRows?.length ?? 0) > 0) ||
      (activeSubTab === "enhancetaggingresults" &&
        enhanceState === "success" &&
        (enhanceRows?.length ?? 0) > 0));

  if (!canReadAssessmentPack && !canReadClassificationResults) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.75,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Impact Assessment
        </Typography>
        {showSyncToTask ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onSyncToTask}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Sync to Task
          </Button>
        ) : null}
      </Box>

      {canReadAssessmentPack ? (
        <AssessmentPackSection regulationId={regulationId} version={version} />
      ) : null}

      {canReadClassificationResults ? (
        <Tabs
          value={activeSubTab}
          onChange={(_, v: ImpactSubTabKey) => setActiveSubTab(v)}
          sx={{
            mb: 2,
            borderBottom: "1px solid #E5E7EB",
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              minHeight: 40,
              fontSize: 13,
              fontWeight: 500,
              color: "#6A7282",
              "&.Mui-selected": { fontWeight: 700, color: "#1447E6" },
            },
            "& .MuiTabs-indicator": { backgroundColor: "#1447E6" },
          }}
        >
          {IMPACT_SUB_TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
      ) : null}

      {canReadClassificationResults && showCollapseToggle ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            mb: 1.75,
          }}
        >
          <Link
            component="button"
            type="button"
            underline="none"
            onClick={toggleAll}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#1447E6",
              cursor: "pointer",
              flex: "none",
              whiteSpace: "nowrap",
            }}
          >
            {allCollapsed ? "Expand All" : "Collapse All"}
          </Link>
        </Box>
      ) : null}

      {canReadClassificationResults && activeSubTab === "taggingresults" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {renderTaggingBody()}
        </Box>
      ) : null}

      {canReadClassificationResults && activeSubTab === "enhancetaggingresults" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {renderEnhanceBody()}
        </Box>
      ) : null}

      {activeSubTab === "assets" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
          {assetGroups.map((g) => (
            <Box key={g.tier}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box
                  component="span"
                  sx={{
                    bgcolor: g.relBg,
                    color: g.relColor,
                    borderRadius: "6px",
                    px: 1.25,
                    py: 0.35,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {g.tier} Relevance
                </Box>
                <Typography sx={{ fontSize: 12, color: "#99A1AF" }}>
                  ({g.count})
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {g.rows.map((ar) => (
                  <Box
                    key={ar.rank}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "10px",
                      bgcolor: "#fff",
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1.5, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#99A1AF",
                            flex: "none",
                            pt: 0.25,
                          }}
                        >
                          #{ar.rank}
                        </Typography>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#101828",
                              fontWeight: 600,
                            }}
                          >
                            {ar.title}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: "#99A1AF", mt: 0.25 }}
                          >
                            {ar.subtitle}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          bgcolor: "#DCFCE7",
                          color: "#166534",
                          borderRadius: "6px",
                          px: 1.25,
                          py: 0.35,
                          fontSize: 11,
                          flex: "none",
                        }}
                      >
                        {ar.status}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: "1px solid #F3F4F6",
                      }}
                    >
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {ar.tags.map((tg) => (
                          <Box
                            key={tg}
                            component="span"
                            sx={{
                              bgcolor: "#EFF6FF",
                              color: "#1447E6",
                              borderRadius: "6px",
                              px: 1.25,
                              py: 0.35,
                              fontSize: 11,
                            }}
                          >
                            {tg}
                          </Box>
                        ))}
                      </Box>
                      <Typography
                        sx={{ fontSize: 11, color: "#99A1AF", flex: "none" }}
                      >
                        Score {ar.score}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}

      {(activeSubTab === "recommendation" ||
        activeSubTab === "consolidated") && (
        <Typography
          sx={{ color: "#6A7282", fontSize: 13, py: 6, textAlign: "center" }}
        >
          No{" "}
          {activeSubTab === "recommendation"
            ? "recommendations"
            : "consolidated results"}{" "}
          available yet.
        </Typography>
      )}

      <ShowAllImpactModal
        open={!!showAllRow}
        row={showAllRow}
        onClose={() => setShowAllRow(null)}
      />
    </Box>
  );
};

export default TaggingImpactAssessmentSection;
